import * as THREE from 'three';
import { KeyCodes } from '../../shared/defines.js';
import { Utils } from '../../shared/utils.js';
import { EventDispatcher } from '../event-dispatcher.js';

export class InputHandler extends EventDispatcher {
  viewer: any;
  renderer: any;
  domElement: HTMLElement;
  enabled: boolean;

  scene: any;
  interactiveScenes: any[];
  interactiveObjects: Set<any>;
  inputListeners: any[];
  blacklist: Set<any>;

  drag: any;
  mouse: THREE.Vector2;

  selection: any[];

  hoveredElements: any[];
  pressedKeys: Record<number, boolean>;

  wheelDelta: number;

  speed: number;

  logMessages: boolean;

  private _boundHandlers: Record<string, any>;
  private _raycaster: THREE.Raycaster | null = null;
  private viewStart: any;

  constructor(viewer: any) {
    super();

    this.viewer = viewer;
    this.renderer = viewer.renderer;
    this.domElement = this.renderer.domElement;
    this.enabled = true;

    this.scene = null;
    this.interactiveScenes = [];
    this.interactiveObjects = new Set();
    this.inputListeners = [];
    this.blacklist = new Set();

    this.drag = null;
    this.mouse = new THREE.Vector2(0, 0);

    this.selection = [];

    this.hoveredElements = [];
    this.pressedKeys = {};

    this.wheelDelta = 0;

    this.speed = 1;

    this.logMessages = false;

    if ((this.domElement as HTMLElement).tabIndex === -1) {
      (this.domElement as HTMLElement).tabIndex = 2222;
    }

    // Store bound handlers for cleanup
    this._boundHandlers = {
      contextmenu: (event: Event) => {
        event.preventDefault();
      },
      click: this.onMouseClick.bind(this),
      mousedown: this.onMouseDown.bind(this),
      mouseup: this.onMouseUp.bind(this),
      mousemove: this.onMouseMove.bind(this),
      mousewheel: this.onMouseWheel.bind(this),
      DOMMouseScroll: this.onMouseWheel.bind(this),
      dblclick: this.onDoubleClick.bind(this),
      keydown: this.onKeyDown.bind(this),
      keyup: this.onKeyUp.bind(this),
      touchstart: this.onTouchStart.bind(this),
      touchend: this.onTouchEnd.bind(this),
      touchmove: this.onTouchMove.bind(this),
    };

    this.domElement.addEventListener('contextmenu', this._boundHandlers.contextmenu, false);
    this.domElement.addEventListener('click', this._boundHandlers.click, false);
    this.domElement.addEventListener('mousedown', this._boundHandlers.mousedown, false);
    this.domElement.addEventListener('mouseup', this._boundHandlers.mouseup, false);
    this.domElement.addEventListener('mousemove', this._boundHandlers.mousemove, false);
    this.domElement.addEventListener('mousewheel', this._boundHandlers.mousewheel, false);
    this.domElement.addEventListener('DOMMouseScroll', this._boundHandlers.DOMMouseScroll, false);
    this.domElement.addEventListener('dblclick', this._boundHandlers.dblclick);
    this.domElement.addEventListener('keydown', this._boundHandlers.keydown);
    this.domElement.addEventListener('keyup', this._boundHandlers.keyup);
    this.domElement.addEventListener('touchstart', this._boundHandlers.touchstart);
    this.domElement.addEventListener('touchend', this._boundHandlers.touchend);
    this.domElement.addEventListener('touchmove', this._boundHandlers.touchmove);
  }

  addInputListener(listener: any): void {
    this.inputListeners.push(listener);
  }

  removeInputListener(listener: any): void {
    this.inputListeners = this.inputListeners.filter(e => e !== listener);
  }

  getSortedListeners(): any[] {
    return this.inputListeners.sort((a, b) => {
      let ia = a.importance !== undefined ? a.importance : 0;
      let ib = b.importance !== undefined ? b.importance : 0;

      return ib - ia;
    });
  }

  onTouchStart(e: TouchEvent): void {
    if (this.logMessages) console.log(this.constructor.name + ': onTouchStart');

    e.preventDefault();

    if (e.touches.length === 1) {
      let rect = this.domElement.getBoundingClientRect();
      let x = e.touches[0].pageX - rect.left;
      let y = e.touches[0].pageY - rect.top;
      this.mouse.set(x, y);

      this.startDragging(null);
    }

    for (let inputListener of this.getSortedListeners()) {
      inputListener.dispatchEvent({
        type: e.type,
        touches: e.touches,
        changedTouches: e.changedTouches,
      });
    }
  }

  onTouchEnd(e: TouchEvent): void {
    if (this.logMessages) console.log(this.constructor.name + ': onTouchEnd');

    e.preventDefault();

    for (let inputListener of this.getSortedListeners()) {
      inputListener.dispatchEvent({
        type: 'drop',
        drag: this.drag,
        viewer: this.viewer,
      });
    }

    this.drag = null;

    for (let inputListener of this.getSortedListeners()) {
      inputListener.dispatchEvent({
        type: e.type,
        touches: e.touches,
        changedTouches: e.changedTouches,
      });
    }
  }

  onTouchMove(e: TouchEvent): void {
    if (this.logMessages) console.log(this.constructor.name + ': onTouchMove');

    e.preventDefault();

    if (e.touches.length === 1) {
      let rect = this.domElement.getBoundingClientRect();
      let x = e.touches[0].pageX - rect.left;
      let y = e.touches[0].pageY - rect.top;
      this.mouse.set(x, y);

      if (this.drag) {
        this.drag.mouse = 1;

        this.drag.lastDrag.x = x - this.drag.end.x;
        this.drag.lastDrag.y = y - this.drag.end.y;

        this.drag.end.set(x, y);

        if (this.logMessages) console.log(this.constructor.name + ': drag: ');
        for (let inputListener of this.getSortedListeners()) {
          inputListener.dispatchEvent({
            type: 'drag',
            drag: this.drag,
            viewer: this.viewer,
          });
        }
      }
    }

    for (let inputListener of this.getSortedListeners()) {
      inputListener.dispatchEvent({
        type: e.type,
        touches: e.touches,
        changedTouches: e.changedTouches,
      });
    }
  }

  onKeyDown(e: KeyboardEvent): void {
    if (this.logMessages) console.log(this.constructor.name + ': onKeyDown');

    // DELETE
    if (e.keyCode === KeyCodes.DELETE && this.selection.length > 0) {
      this.dispatchEvent({
        type: 'delete',
        selection: this.selection,
      });

      this.deselectAll();
    }

    this.dispatchEvent({
      type: 'keydown',
      keyCode: e.keyCode,
      event: e,
    });

    this.pressedKeys[e.keyCode] = true;
  }

  onKeyUp(e: KeyboardEvent): void {
    if (this.logMessages) console.log(this.constructor.name + ': onKeyUp');

    delete this.pressedKeys[e.keyCode];

    e.preventDefault();
  }

  onDoubleClick(e: MouseEvent): void {
    if (this.logMessages) console.log(this.constructor.name + ': onDoubleClick');

    let consumed = false;
    for (let hovered of this.hoveredElements) {
      if (hovered._listeners && hovered._listeners['dblclick']) {
        hovered.object.dispatchEvent({
          type: 'dblclick',
          mouse: this.mouse,
          object: hovered.object,
        });
        consumed = true;
        break;
      }
    }

    if (!consumed) {
      for (let inputListener of this.getSortedListeners()) {
        inputListener.dispatchEvent({
          type: 'dblclick',
          mouse: this.mouse,
          object: null,
        });
      }
    }

    e.preventDefault();
  }

  onMouseClick(e: MouseEvent): void {
    if (this.logMessages) console.log(this.constructor.name + ': onMouseClick');

    e.preventDefault();
  }

  onMouseDown(e: MouseEvent): void {
    if (this.logMessages) console.log(this.constructor.name + ': onMouseDown');

    e.preventDefault();

    let consumed = false;
    let consume = () => {
      return (consumed = true);
    };
    if (this.hoveredElements.length === 0) {
      for (let inputListener of this.getSortedListeners()) {
        inputListener.dispatchEvent({
          type: 'mousedown',
          viewer: this.viewer,
          mouse: this.mouse,
        });
      }
    } else {
      for (let hovered of this.hoveredElements) {
        let object = hovered.object;
        object.dispatchEvent({
          type: 'mousedown',
          viewer: this.viewer,
          consume: consume,
        });

        if (consumed) {
          break;
        }
      }
    }

    if (!this.drag) {
      let target = this.hoveredElements.find(
        (el: any) =>
          el.object._listeners &&
          el.object._listeners['drag'] &&
          el.object._listeners['drag'].length > 0,
      );

      if (target) {
        this.startDragging(target.object, { location: target.point });
      } else {
        this.startDragging(null);
      }
    }

    if (this.scene) {
      this.viewStart = this.scene.view.clone();
    }
  }

  onMouseUp(e: MouseEvent): void {
    if (this.logMessages) console.log(this.constructor.name + ': onMouseUp');

    e.preventDefault();

    let noMovement = this.getNormalizedDrag().length() === 0;

    let consumed = false;
    let consume = () => {
      return (consumed = true);
    };
    if (this.hoveredElements.length === 0) {
      for (let inputListener of this.getSortedListeners()) {
        inputListener.dispatchEvent({
          type: 'mouseup',
          viewer: this.viewer,
          mouse: this.mouse,
          consume: consume,
        });

        if (consumed) {
          break;
        }
      }
    } else {
      let hovered: any = null;
      for (let i = 0; i < this.hoveredElements.length; i++) {
        let obj = this.hoveredElements[i].object;
        if (obj._listeners && obj._listeners['mouseup']) {
          hovered = obj;
          break;
        }
      }
      if (hovered) {
        hovered.dispatchEvent({
          type: 'mouseup',
          viewer: this.viewer,
          consume: consume,
        });
      }
    }

    if (this.drag) {
      if (this.drag.object) {
        if (this.logMessages)
          console.log(`${this.constructor.name}: drop ${this.drag.object.name}`);
        this.drag.object.dispatchEvent({
          type: 'drop',
          drag: this.drag,
          viewer: this.viewer,
        });
      } else {
        for (let inputListener of this.getSortedListeners()) {
          inputListener.dispatchEvent({
            type: 'drop',
            drag: this.drag,
            viewer: this.viewer,
          });
        }
      }

      // check for a click
      let clicked = false;
      for (let i = 0; i < this.hoveredElements.length; i++) {
        if (this.hoveredElements[i].object === this.drag.object) {
          clicked = true;
          break;
        }
      }
      if (clicked) {
        if (this.logMessages)
          console.log(`${this.constructor.name}: click ${this.drag.object.name}`);
        this.drag.object.dispatchEvent({
          type: 'click',
          viewer: this.viewer,
          consume: consume,
        });
      }

      this.drag = null;
    }

    if (!consumed) {
      if (e.button === THREE.MOUSE.LEFT) {
        if (noMovement) {
          let selectable = this.hoveredElements.find(
            (el: any) => el.object._listeners && el.object._listeners['select'],
          );

          if (selectable) {
            selectable = selectable.object;

            if (this.isSelected(selectable)) {
              this.selection.filter(e => e !== selectable).forEach(e => this.toggleSelection(e));
            } else {
              this.deselectAll();
              this.toggleSelection(selectable);
            }
          } else {
            this.deselectAll();
          }
        }
      } else if (e.button === THREE.MOUSE.RIGHT && noMovement) {
        this.deselectAll();
      }
    }
  }

  onMouseMove(e: MouseEvent): void {
    e.preventDefault();

    let rect = this.domElement.getBoundingClientRect();
    let x = e.clientX - rect.left;
    let y = e.clientY - rect.top;
    this.mouse.set(x, y);

    let hoveredElements = this.getHoveredElements();
    if (hoveredElements.length > 0) {
      let names = hoveredElements.map((h: any) => h.object.name).join(', ');
      if (this.logMessages)
        console.log(`${this.constructor.name}: onMouseMove; hovered: '${names}'`);
    }

    if (this.drag) {
      this.drag.mouse = e.buttons;

      this.drag.lastDrag.x = x - this.drag.end.x;
      this.drag.lastDrag.y = y - this.drag.end.y;

      this.drag.end.set(x, y);

      if (this.drag.object) {
        if (this.logMessages)
          console.log(this.constructor.name + ': drag: ' + this.drag.object.name);
        this.drag.object.dispatchEvent({
          type: 'drag',
          drag: this.drag,
          viewer: this.viewer,
        });
      } else {
        if (this.logMessages) console.log(this.constructor.name + ': drag: ');

        let dragConsumed = false;
        for (let inputListener of this.getSortedListeners()) {
          inputListener.dispatchEvent({
            type: 'drag',
            drag: this.drag,
            viewer: this.viewer,
            consume: () => {
              dragConsumed = true;
            },
          });

          if (dragConsumed) {
            break;
          }
        }
      }
    } else {
      let curr = hoveredElements.length > 0 ? hoveredElements[0].object : null;
      let prev = this.hoveredElements.length > 0 ? this.hoveredElements[0].object : null;

      if (curr !== prev) {
        if (curr) {
          if (this.logMessages) console.log(`${this.constructor.name}: mouseover: ${curr.name}`);
          curr.dispatchEvent({
            type: 'mouseover',
            object: curr,
          });
        }
        if (prev) {
          if (this.logMessages) console.log(`${this.constructor.name}: mouseleave: ${prev.name}`);
          prev.dispatchEvent({
            type: 'mouseleave',
            object: prev,
          });
        }
      }

      if (hoveredElements.length > 0) {
        let object: any = null;
        for (let i = 0; i < hoveredElements.length; i++) {
          let obj = hoveredElements[i].object;
          if (obj._listeners && obj._listeners['mousemove']) {
            object = obj;
            break;
          }
        }

        if (object) {
          object.dispatchEvent({
            type: 'mousemove',
            object: object,
          });
        }
      }
    }

    this.hoveredElements = hoveredElements;
  }

  onMouseWheel(e: any): void {
    if (!this.enabled) return;

    if (this.logMessages) console.log(this.constructor.name + ': onMouseWheel');

    e.preventDefault();

    let delta = 0;
    if (e.wheelDelta !== undefined) {
      // WebKit / Opera / Explorer 9
      delta = e.wheelDelta;
    } else if (e.detail !== undefined) {
      // Firefox
      delta = -e.detail;
    }

    let ndelta = Math.sign(delta);

    if (this.hoveredElements.length > 0 && (this.hoveredElements as any)[0]?.object) {
      (this.hoveredElements as any)[0].object.dispatchEvent({
        type: 'mousewheel',
        delta: ndelta,
        object: (this.hoveredElements as any)[0].object,
      });
    } else {
      for (let inputListener of this.getSortedListeners()) {
        inputListener.dispatchEvent({
          type: 'mousewheel',
          delta: ndelta,
          object: null,
        });
      }
    }
  }

  startDragging(object: any, args: any = null): void {
    let name = object ? object.name : 'no name';
    if (this.logMessages) console.log(`${this.constructor.name}: startDragging: '${name}'`);

    this.drag = {
      start: this.mouse.clone(),
      end: this.mouse.clone(),
      lastDrag: new THREE.Vector2(0, 0),
      startView: this.scene.view.clone(),
      object: object,
    };

    if (args) {
      for (let key of Object.keys(args)) {
        this.drag[key] = args[key];
      }
    }
  }

  getMousePointCloudIntersection(mouse?: THREE.Vector2): any {
    return Utils.getMousePointCloudIntersection(
      this.mouse,
      this.scene.getActiveCamera(),
      this.viewer,
      this.scene.pointclouds,
    );
  }

  toggleSelection(object: any): void {
    let oldSelection = this.selection;

    let index = this.selection.indexOf(object);

    if (index === -1) {
      this.selection.push(object);
      object.dispatchEvent({
        type: 'select',
      });
    } else {
      this.selection.splice(index, 1);
      object.dispatchEvent({
        type: 'deselect',
      });
    }

    this.dispatchEvent({
      type: 'selection_changed',
      oldSelection: oldSelection,
      selection: this.selection,
    });
  }

  deselect(object: any): void {
    let oldSelection = this.selection;

    let index = this.selection.indexOf(object);

    if (index >= 0) {
      this.selection.splice(index, 1);
      object.dispatchEvent({
        type: 'deselect',
      });

      this.dispatchEvent({
        type: 'selection_changed',
        oldSelection: oldSelection,
        selection: this.selection,
      });
    }
  }

  deselectAll(): void {
    for (let object of this.selection) {
      object.dispatchEvent({
        type: 'deselect',
      });
    }

    let oldSelection = this.selection;

    if (this.selection.length > 0) {
      this.selection = [];
      this.dispatchEvent({
        type: 'selection_changed',
        oldSelection: oldSelection,
        selection: this.selection,
      });
    }
  }

  isSelected(object: any): boolean {
    let index = this.selection.indexOf(object);

    return index !== -1;
  }

  registerInteractiveObject(object: any): void {
    this.interactiveObjects.add(object);
  }

  removeInteractiveObject(object: any): void {
    this.interactiveObjects.delete(object);
  }

  registerInteractiveScene(scene: any): void {
    let index = this.interactiveScenes.indexOf(scene);
    if (index === -1) {
      this.interactiveScenes.push(scene);
    }
  }

  unregisterInteractiveScene(scene: any): void {
    let index = this.interactiveScenes.indexOf(scene);
    if (index > -1) {
      this.interactiveScenes.splice(index, 1);
    }
  }

  getHoveredElement(): any {
    let hoveredElements = this.getHoveredElements();
    if (hoveredElements.length > 0) {
      return hoveredElements[0];
    } else {
      return null;
    }
  }

  getHoveredElements(): any[] {
    let scenes = this.interactiveScenes.concat(this.scene.scene);

    let interactableListeners = [
      'mouseup',
      'mousemove',
      'mouseover',
      'mouseleave',
      'drag',
      'drop',
      'click',
      'select',
      'deselect',
    ];
    let interactables: any[] = [];
    for (let scene of scenes) {
      scene.traverseVisible((node: any) => {
        if (node._listeners && node.visible && !this.blacklist.has(node)) {
          for (let i = 0; i < interactableListeners.length; i++) {
            if (node._listeners[interactableListeners[i]] !== undefined) {
              interactables.push(node);
              break;
            }
          }
        }
      });
    }

    let camera = this.scene.getActiveCamera();
    let ray = Utils.mouseToRay(
      this.mouse,
      camera,
      this.domElement.clientWidth,
      this.domElement.clientHeight,
    );

    if (!this._raycaster) {
      this._raycaster = new THREE.Raycaster();
    }
    this._raycaster.ray.set(ray.origin, ray.direction);
    this._raycaster.params.Line!.threshold = 0.2;

    let intersections = this._raycaster.intersectObjects(
      interactables.filter(o => o.visible),
      false,
    );

    return intersections;
  }

  setScene(scene: any): void {
    this.deselectAll();

    this.scene = scene;
  }

  update(delta: number): void {}

  getNormalizedDrag(): THREE.Vector2 {
    if (!this.drag) {
      return new THREE.Vector2(0, 0);
    }

    let diff = new THREE.Vector2().subVectors(this.drag.end, this.drag.start);

    diff.x = diff.x / this.domElement.clientWidth;
    diff.y = diff.y / this.domElement.clientHeight;

    return diff;
  }

  getNormalizedLastDrag(): THREE.Vector2 {
    if (!this.drag) {
      return new THREE.Vector2(0, 0);
    }

    let lastDrag = this.drag.lastDrag.clone();

    lastDrag.x = lastDrag.x / this.domElement.clientWidth;
    lastDrag.y = lastDrag.y / this.domElement.clientHeight;

    return lastDrag;
  }

  dispose(): void {
    this.domElement.removeEventListener('contextmenu', this._boundHandlers.contextmenu, false);
    this.domElement.removeEventListener('click', this._boundHandlers.click, false);
    this.domElement.removeEventListener('mousedown', this._boundHandlers.mousedown, false);
    this.domElement.removeEventListener('mouseup', this._boundHandlers.mouseup, false);
    this.domElement.removeEventListener('mousemove', this._boundHandlers.mousemove, false);
    this.domElement.removeEventListener('mousewheel', this._boundHandlers.mousewheel, false);
    this.domElement.removeEventListener(
      'DOMMouseScroll',
      this._boundHandlers.DOMMouseScroll,
      false,
    );
    this.domElement.removeEventListener('dblclick', this._boundHandlers.dblclick);
    this.domElement.removeEventListener('keydown', this._boundHandlers.keydown);
    this.domElement.removeEventListener('keyup', this._boundHandlers.keyup);
    this.domElement.removeEventListener('touchstart', this._boundHandlers.touchstart);
    this.domElement.removeEventListener('touchend', this._boundHandlers.touchend);
    this.domElement.removeEventListener('touchmove', this._boundHandlers.touchmove);
    this._boundHandlers = null!;
    this.inputListeners = [];
    this.interactiveScenes = [];
    this.interactiveObjects.clear();
    this.hoveredElements = [];
    this.selection = [];
  }
}
